import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import {
  Monitor,
  Tablet,
  Smartphone,
  X,
  Maximize2,
  Minimize2,
  ExternalLink,
  RotateCw,
} from "lucide-react";
import { INJECTED_PREVIEW_BRIDGE } from "./preview-bridge";
import { createPreviewHubHtml } from "./preview-hub";

interface LivePreviewPanelProps {
  html: string;
  isOpen: boolean;
  onToggle: () => void;
  activeSectionType?: string;
  onSectionClick?: (sectionType: string) => void;
  isDragging?: boolean;
  pages?: Array<{ id: string; title: string; slug: string; isHome: boolean; sortOrder: number; visible?: boolean }>;
  activeSlug?: string;
  onNavigateSlug?: (slug: string) => void;
  // Legacy / optional props for backwards compatibility
  profile?: any;
  userTier?: string;
  onUpgradeClick?: () => void;
  onUpdateSeo?: (seo: any) => void;
}

type Viewport = "desktop" | "tablet" | "mobile";

export function LivePreviewPanel({
  html,
  isOpen,
  onToggle,
  activeSectionType,
  onSectionClick,
  isDragging = false,
  pages,
  activeSlug,
  onNavigateSlug,
  profile,
}: LivePreviewPanelProps) {
  // Drawer Viewport State
  const [viewport, setViewport] = useState<Viewport>("desktop");
  const [drawerDesktopMode, setDrawerDesktopMode] = useState<"scaled" | "fluid">("scaled");
  const drawerCanvasRef = useRef<HTMLDivElement>(null);
  const [drawerCanvasSize, setDrawerCanvasSize] = useState({ width: 480, height: 700 });

  // Fullscreen Viewport State
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [fullscreenViewport, setFullscreenViewport] = useState<Viewport>("desktop");
  const [fullscreenOrientation, setFullscreenOrientation] = useState<"portrait" | "landscape">("portrait");
  const [fullscreenZoom, setFullscreenZoom] = useState<"fit" | "100%" | "75%">("fit");
  const fullscreenCanvasRef = useRef<HTMLDivElement>(null);
  const [fullscreenCanvasSize, setFullscreenCanvasSize] = useState({ width: 1200, height: 800 });

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const fullscreenIframeRef = useRef<HTMLIFrameElement>(null);
  const [isIframeReady, setIsIframeReady] = useState(false);
  const [isFullscreenIframeReady, setIsFullscreenIframeReady] = useState(false);

  const latestHtmlRef = useRef(html);
  latestHtmlRef.current = html;

  const getHtmlWithBridge = (rawHtml: string) => {
    if (!rawHtml) {
      return `<!DOCTYPE html><html><head><meta charset="UTF-8"><meta name="viewport" content="width=device-width, initial-scale=1.0"><style>body{margin:0;padding:2rem;font-family:-apple-system,BlinkMacSystemFont,sans-serif;color:#94a3b8;display:flex;align-items:center;justify-content:center;min-height:50vh;}</style></head><body><div>Loading preview...</div>${INJECTED_PREVIEW_BRIDGE}</body></html>`;
    }
    if (rawHtml.includes("</body>")) {
      return rawHtml.replace("</body>", `${INJECTED_PREVIEW_BRIDGE}</body>`);
    }
    return rawHtml + INJECTED_PREVIEW_BRIDGE;
  };

  // Track drawer canvas dimensions
  useEffect(() => {
    if (!drawerCanvasRef.current || !isOpen) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.contentRect) {
          setDrawerCanvasSize({
            width: entry.contentRect.width,
            height: entry.contentRect.height,
          });
        }
      }
    });
    ro.observe(drawerCanvasRef.current);
    return () => ro.disconnect();
  }, [isOpen]);

  // Track fullscreen canvas dimensions
  useEffect(() => {
    if (!fullscreenCanvasRef.current || !isFullscreen) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.contentRect) {
          setFullscreenCanvasSize({
            width: entry.contentRect.width,
            height: entry.contentRect.height,
          });
        }
      }
    });
    ro.observe(fullscreenCanvasRef.current);
    return () => ro.disconnect();
  }, [isFullscreen]);

  // Reset ready state when panel is closed so it re-initializes cleanly
  useEffect(() => {
    if (!isOpen) setIsIframeReady(false);
  }, [isOpen]);

  useEffect(() => {
    if (iframeRef.current?.contentWindow && latestHtmlRef.current) {
      iframeRef.current.contentWindow.postMessage({ type: "UPDATE_HTML", html: latestHtmlRef.current }, "*");
    }
  }, [viewport]);

  useEffect(() => {
    if (!isFullscreen) setIsFullscreenIframeReady(false);
  }, [isFullscreen]);

  // Handle ESC key to exit fullscreen
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isFullscreen) {
        setIsFullscreen(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isFullscreen]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === "PREVIEW_READY") {
        if (event.source === iframeRef.current?.contentWindow) {
          setIsIframeReady(true);
        }
        if (event.source === fullscreenIframeRef.current?.contentWindow) {
          setIsFullscreenIframeReady(true);
        }
        try {
          (event.source as WindowProxy)?.postMessage({ type: "UPDATE_HTML", html: latestHtmlRef.current }, "*");
        } catch {}
      }
      if (event.data?.type === "SECTION_CLICKED" && event.data.sectionId) {
        onSectionClick?.(event.data.sectionId);
      }
      if (event.data?.type === "PREVIEW_NAVIGATE" && typeof event.data.slug === "string") {
        onNavigateSlug?.(event.data.slug);
      }
      if (event.data?.type === "EXIT_FULLSCREEN") {
        setIsFullscreen(false);
      }
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [onSectionClick, onNavigateSlug]);

  useEffect(() => {
    if (!isOpen || !isIframeReady || !activeSectionType || !iframeRef.current?.contentWindow) return;
    iframeRef.current.contentWindow.postMessage(
      { type: "SCROLL_TO_SECTION", sectionId: activeSectionType },
      "*"
    );
  }, [activeSectionType, isIframeReady, isOpen]);

  // Send HTML update whenever html changes or iframe becomes ready
  useEffect(() => {
    if (!isOpen || !html || !isIframeReady) return;
    if (iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.postMessage({ type: "UPDATE_HTML", html }, "*");
    }
  }, [html, isOpen, isIframeReady]);

  // Update fullscreen preview iframe
  useEffect(() => {
    if (!isFullscreen || !html || !isFullscreenIframeReady) return;
    if (fullscreenIframeRef.current?.contentWindow) {
      fullscreenIframeRef.current.contentWindow.postMessage({ type: "UPDATE_HTML", html }, "*");
    }
  }, [html, isFullscreen, isFullscreenIframeReady]);

  const handleOpenFullscreen = () => {
    setFullscreenViewport(viewport);
    setIsFullscreen(true);
  };

  const handleOpenInNewTab = () => {
    const hubHtml = createPreviewHubHtml(
      latestHtmlRef.current || html,
      profile?.fullName || "Profolio Preview",
      profile?.subdomain
    );
    const blob = new Blob([hubHtml], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank");
  };

  const handleRefresh = () => {
    const current = latestHtmlRef.current || html;
    if (iframeRef.current?.contentWindow && current) {
      iframeRef.current.contentWindow.postMessage({ type: "UPDATE_HTML", html: current }, "*");
    }
    if (fullscreenIframeRef.current?.contentWindow && current) {
      fullscreenIframeRef.current.contentWindow.postMessage({ type: "UPDATE_HTML", html: current }, "*");
    }
  };

  const handleIframeLoad = () => {
    setIsIframeReady(true);
    const contentWin = iframeRef.current?.contentWindow;
    const current = latestHtmlRef.current || html;
    if (current && contentWin) {
      contentWin.postMessage({ type: "UPDATE_HTML", html: current }, "*");
      setTimeout(() => {
        contentWin.postMessage({ type: "UPDATE_HTML", html: current }, "*");
      }, 50);
      setTimeout(() => {
        contentWin.postMessage({ type: "UPDATE_HTML", html: current }, "*");
      }, 200);
    }
  };

  const handleFullscreenIframeLoad = () => {
    setIsFullscreenIframeReady(true);
    const contentWin = fullscreenIframeRef.current?.contentWindow;
    const current = latestHtmlRef.current || html;
    if (current && contentWin) {
      contentWin.postMessage({ type: "UPDATE_HTML", html: current }, "*");
      setTimeout(() => {
        contentWin.postMessage({ type: "UPDATE_HTML", html: current }, "*");
      }, 50);
      setTimeout(() => {
        contentWin.postMessage({ type: "UPDATE_HTML", html: current }, "*");
      }, 200);
    }
  };

  if (!isOpen) return null;

  // Compute Scaled Drawer Dimensions
  const drawerAvailableW = Math.max(drawerCanvasSize.width - 24, 200);
  const drawerAvailableH = Math.max(drawerCanvasSize.height - 24, 300);

  // Desktop scale factor: 1280px standard
  const drawerDesktopScale = Math.max(0.15, Math.min(1, drawerAvailableW / 1280));
  // Tablet scale factor: 768px standard
  const drawerTabletScale = Math.max(0.2, Math.min(1, drawerAvailableW / 768));
  // Mobile scale factor: 375px standard
  const drawerMobileScale = Math.max(0.2, Math.min(1, drawerAvailableW / 375));

  return (
    <>
      <div className="flex h-full flex-col bg-muted/10 select-none">
        {/* Preview Toolbar */}
        <div className="flex items-center justify-between border-b border-border/60 bg-card px-3 py-1.5 shadow-xs z-10 shrink-0">
          <div className="flex items-center gap-1.5">
            {/* Segmented Viewport Switcher */}
            <div className="flex items-center gap-0.5 bg-muted/60 p-0.5 rounded-lg border border-border/50">
              <Button
                variant={viewport === "desktop" ? "default" : "ghost"}
                size="sm"
                onClick={() => setViewport("desktop")}
                className="h-6 px-2 text-[11px] gap-1 rounded-md"
                title="Desktop view"
              >
                <Monitor className="h-3 w-3" />
                <span className="hidden sm:inline">Desktop</span>
              </Button>
              <Button
                variant={viewport === "tablet" ? "default" : "ghost"}
                size="sm"
                onClick={() => setViewport("tablet")}
                className="h-6 w-6 p-0 rounded-md"
                title="Tablet view (768px)"
              >
                <Tablet className="h-3 w-3" />
              </Button>
              <Button
                variant={viewport === "mobile" ? "default" : "ghost"}
                size="sm"
                onClick={() => setViewport("mobile")}
                className="h-6 w-6 p-0 rounded-md"
                title="Mobile view (375px)"
              >
                <Smartphone className="h-3 w-3" />
              </Button>
            </div>

            {/* Desktop Ratio Toggle (Scaled 1280px vs Fluid) */}
            {viewport === "desktop" && (
              <div className="hidden sm:flex items-center gap-0.5 bg-muted/40 p-0.5 rounded-md border border-border/40 text-[10px]">
                <button
                  type="button"
                  onClick={() => setDrawerDesktopMode("scaled")}
                  className={`px-1.5 py-0.5 rounded font-medium transition-colors cursor-pointer ${
                    drawerDesktopMode === "scaled"
                      ? "bg-background text-foreground shadow-xs font-semibold"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                  title="True 1280px desktop ratio scaled to fit"
                >
                  1280px Scale
                </button>
                <button
                  type="button"
                  onClick={() => setDrawerDesktopMode("fluid")}
                  className={`px-1.5 py-0.5 rounded font-medium transition-colors cursor-pointer ${
                    drawerDesktopMode === "fluid"
                      ? "bg-background text-foreground shadow-xs font-semibold"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                  title="Fluid responsive 100% width"
                >
                  Fluid
                </button>
              </div>
            )}
          </div>

          {/* Multi-Page Selector */}
          {pages && pages.length > 1 && (
            <div className="flex items-center gap-1 bg-muted/60 px-2 py-0.5 rounded-lg border border-border/50 text-xs">
              <span className="text-[10px] text-muted-foreground font-medium hidden sm:inline">Page:</span>
              <select
                value={activeSlug ?? ""}
                onChange={(e) => onNavigateSlug?.(e.target.value)}
                className="bg-transparent text-[11px] font-semibold text-foreground py-0.5 px-0.5 rounded cursor-pointer border-0 outline-none"
                title="Switch preview page"
              >
                {pages.map((p) => (
                  <option key={p.id} value={p.slug} className="bg-popover text-popover-foreground">
                    {p.title} {p.isHome ? "(Home)" : `(/${p.slug})`}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Quick Actions */}
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRefresh}
              className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
              title="Refresh Preview"
            >
              <RotateCw className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleOpenInNewTab}
              className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
              title="Open Viewport Preview in New Tab"
            >
              <ExternalLink className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleOpenFullscreen}
              className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
              title="Full Window Preview (100vw)"
            >
              <Maximize2 className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onToggle}
              className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
              title="Close Preview Panel"
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>

        {/* Viewport Canvas */}
        <div
          ref={drawerCanvasRef}
          className="flex-1 overflow-auto bg-muted/20 p-3 flex items-center justify-center relative"
        >
          {drawerDesktopMode === "fluid" && viewport === "desktop" ? (
            <div className="w-full h-full rounded-xl shadow-xs border border-border bg-card overflow-hidden">
              <iframe
                ref={iframeRef}
                srcDoc={getHtmlWithBridge(latestHtmlRef.current)}
                onLoad={handleIframeLoad}
                className={`w-full h-full border-none block ${isDragging ? "pointer-events-none" : ""}`}
                title="Fluid Desktop Preview"
                sandbox="allow-scripts"
              />
            </div>
          ) : (
            (() => {
              let containerW = drawerAvailableW;
              let containerH = drawerAvailableH;
              let iframeTargetW = 1280;
              let scale = drawerDesktopScale;
              let deviceLabel = "1280px Desktop";
              let Icon = Monitor;

              if (viewport === "desktop") {
                containerW = drawerAvailableW;
                containerH = drawerAvailableH;
                iframeTargetW = 1280;
                scale = drawerDesktopScale;
                deviceLabel = "1280px Desktop";
                Icon = Monitor;
              } else if (viewport === "tablet") {
                containerW = Math.round(768 * drawerTabletScale);
                containerH = Math.min(Math.round(1024 * drawerTabletScale), drawerAvailableH);
                iframeTargetW = 768;
                scale = drawerTabletScale;
                deviceLabel = "768px Tablet";
                Icon = Tablet;
              } else if (viewport === "mobile") {
                containerW = Math.round(375 * drawerMobileScale);
                containerH = Math.min(Math.round(812 * drawerMobileScale), drawerAvailableH);
                iframeTargetW = 375;
                scale = drawerMobileScale;
                deviceLabel = "375px Mobile";
                Icon = Smartphone;
              }

              const iframeH = Math.round((containerH - 24) / scale);

              return (
                <div
                  style={{ width: `${containerW}px`, height: `${containerH}px` }}
                  className={`border border-border shadow-md bg-card overflow-hidden flex flex-col mx-auto shrink-0 relative transition-all duration-200 ${
                    viewport === "mobile" ? "rounded-2xl" : "rounded-xl"
                  }`}
                >
                  {/* Mini Chrome Bar */}
                  <div className="h-6 bg-muted/70 border-b border-border/60 px-2.5 flex items-center justify-between select-none text-[10px] text-muted-foreground shrink-0">
                    <div className="flex items-center gap-1.5">
                      <Icon className="h-3 w-3 opacity-70" />
                      <span className="font-mono text-[9px] opacity-80">{deviceLabel}</span>
                    </div>
                    <span className="font-mono text-[9px] bg-background/80 px-1.5 py-0.2 rounded border border-border/40">
                      {Math.round(scale * 100)}%
                    </span>
                  </div>
                  <div className="flex-1 overflow-hidden relative bg-card">
                    <iframe
                      ref={iframeRef}
                      srcDoc={getHtmlWithBridge(latestHtmlRef.current)}
                      onLoad={handleIframeLoad}
                      style={{
                        width: `${iframeTargetW}px`,
                        height: `${iframeH}px`,
                        transform: `scale(${scale})`,
                        transformOrigin: "top left",
                      }}
                      className={`border-none block ${isDragging ? "pointer-events-none" : ""}`}
                      title={`${viewport} Preview`}
                      sandbox="allow-scripts"
                    />
                  </div>
                </div>
              );
            })()
          )}
        </div>
      </div>

      {/* Fullscreen Modal Overlay */}
      {isFullscreen && (
        <div className="fixed inset-0 z-50 flex flex-col bg-zinc-950/95 backdrop-blur-md animate-in fade-in duration-200">
          {/* Modal Header */}
          <div className="flex items-center justify-between px-6 py-2.5 bg-zinc-900 border-b border-zinc-800 text-white shadow-md select-none shrink-0">
            <div className="flex items-center gap-3">
              <span className="text-sm font-semibold tracking-tight text-zinc-100">Full Window Preview</span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-zinc-800 text-zinc-400 font-mono">
                {fullscreenViewport === "desktop"
                  ? "1440 × 900 (Desktop)"
                  : fullscreenViewport === "tablet"
                  ? fullscreenOrientation === "portrait" ? "768 × 1024 (Tablet)" : "1024 × 768 (Tablet Landscape)"
                  : fullscreenOrientation === "portrait" ? "390 × 844 (Mobile)" : "844 × 390 (Mobile Landscape)"}
              </span>
            </div>

            {/* Viewport & Device Controls */}
            <div className="flex items-center gap-2">
              {/* Device Selector */}
              <div className="flex items-center gap-1 bg-zinc-800/80 p-1 rounded-lg border border-zinc-700">
                <Button
                  variant={fullscreenViewport === "desktop" ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setFullscreenViewport("desktop")}
                  className={`h-7 px-3 text-xs gap-1.5 ${
                    fullscreenViewport === "desktop"
                      ? "bg-white text-zinc-900 font-medium"
                      : "text-zinc-400 hover:text-white"
                  }`}
                >
                  <Monitor className="h-3.5 w-3.5" />
                  Desktop
                </Button>
                <Button
                  variant={fullscreenViewport === "tablet" ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setFullscreenViewport("tablet")}
                  className={`h-7 px-3 text-xs gap-1.5 ${
                    fullscreenViewport === "tablet"
                      ? "bg-white text-zinc-900 font-medium"
                      : "text-zinc-400 hover:text-white"
                  }`}
                >
                  <Tablet className="h-3.5 w-3.5" />
                  Tablet
                </Button>
                <Button
                  variant={fullscreenViewport === "mobile" ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setFullscreenViewport("mobile")}
                  className={`h-7 px-3 text-xs gap-1.5 ${
                    fullscreenViewport === "mobile"
                      ? "bg-white text-zinc-900 font-medium"
                      : "text-zinc-400 hover:text-white"
                  }`}
                >
                  <Smartphone className="h-3.5 w-3.5" />
                  Mobile
                </Button>
              </div>

              {/* Orientation Toggle (Tablet & Mobile) */}
              {fullscreenViewport !== "desktop" && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    setFullscreenOrientation((prev) => (prev === "portrait" ? "landscape" : "portrait"))
                  }
                  className="h-7 px-2.5 text-xs gap-1.5 bg-zinc-800/80 border border-zinc-700 text-zinc-300 hover:text-white"
                  title="Rotate Viewport Orientation"
                >
                  <RotateCw className="h-3 w-3" />
                  <span className="capitalize">{fullscreenOrientation}</span>
                </Button>
              )}

              {/* Zoom Controls */}
              <div className="flex items-center gap-0.5 bg-zinc-800/80 p-0.5 rounded-lg border border-zinc-700 text-xs">
                <button
                  type="button"
                  onClick={() => setFullscreenZoom("fit")}
                  className={`px-2 py-1 rounded text-xs transition-colors cursor-pointer ${
                    fullscreenZoom === "fit" ? "bg-white text-zinc-900 font-medium" : "text-zinc-400 hover:text-white"
                  }`}
                >
                  Fit
                </button>
                <button
                  type="button"
                  onClick={() => setFullscreenZoom("100%")}
                  className={`px-2 py-1 rounded text-xs transition-colors cursor-pointer ${
                    fullscreenZoom === "100%" ? "bg-white text-zinc-900 font-medium" : "text-zinc-400 hover:text-white"
                  }`}
                >
                  100%
                </button>
                <button
                  type="button"
                  onClick={() => setFullscreenZoom("75%")}
                  className={`px-2 py-1 rounded text-xs transition-colors cursor-pointer ${
                    fullscreenZoom === "75%" ? "bg-white text-zinc-900 font-medium" : "text-zinc-400 hover:text-white"
                  }`}
                >
                  75%
                </button>
              </div>

              {/* Multi-Page Selector */}
              {pages && pages.length > 1 && (
                <div className="flex items-center gap-1.5 bg-zinc-800/80 px-2.5 py-1 rounded-lg border border-zinc-700 text-xs">
                  <span className="text-[11px] text-zinc-400 font-medium">Page:</span>
                  <select
                    value={activeSlug ?? ""}
                    onChange={(e) => onNavigateSlug?.(e.target.value)}
                    className="bg-transparent text-xs font-semibold text-zinc-100 py-0.5 px-0.5 rounded cursor-pointer border-0 outline-none"
                    title="Switch preview page"
                  >
                    {pages.map((p) => (
                      <option key={p.id} value={p.slug} className="bg-zinc-900 text-zinc-100">
                        {p.title} {p.isHome ? "(Home)" : `(/${p.slug})`}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleOpenInNewTab}
                className="h-8 text-xs gap-1.5 bg-zinc-800 border-zinc-700 text-zinc-200 hover:bg-zinc-700 hover:text-white"
              >
                <ExternalLink className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Open in New Tab</span>
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={() => setIsFullscreen(false)}
                className="h-8 px-3 text-xs gap-1.5 bg-primary text-primary-foreground hover:bg-primary/90 font-medium shadow-xs"
                title="Exit Fullscreen Preview (Esc)"
              >
                <Minimize2 className="h-3.5 w-3.5" />
                <span>Exit Preview</span>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsFullscreen(false)}
                className="h-8 w-8 p-0 text-zinc-400 hover:text-white hover:bg-zinc-800"
                title="Close (Esc)"
                aria-label="Close fullscreen preview"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Modal Body Canvas */}
          <div
            ref={fullscreenCanvasRef}
            className="flex-1 overflow-auto p-6 flex flex-col items-center min-h-0 bg-zinc-950/60 relative"
          >
            {(() => {
              const isLand = fullscreenOrientation === "landscape";
              let targetW = 1440;
              let viewportH = 900;

              if (fullscreenViewport === "desktop") {
                targetW = 1440;
                viewportH = 900;
              } else if (fullscreenViewport === "tablet") {
                targetW = isLand ? 1024 : 768;
                viewportH = isLand ? 768 : 1024;
              } else if (fullscreenViewport === "mobile") {
                targetW = isLand ? 844 : 390;
                viewportH = isLand ? 390 : 844;
              }

              const chromeH = 32;
              const totalH = viewportH + chromeH;

              const canvasW = Math.max(fullscreenCanvasSize.width - 48, 200);
              const canvasH = Math.max(fullscreenCanvasSize.height - 48, 200);

              const scale =
                fullscreenZoom === "100%"
                  ? 1
                  : fullscreenZoom === "75%"
                  ? 0.75
                  : Math.min(1, canvasW / targetW, canvasH / totalH);

              return (
                <div
                  style={{
                    width: `${Math.round(targetW * scale)}px`,
                    height: `${Math.round(totalH * scale)}px`,
                    margin: "auto",
                    flexShrink: 0,
                    position: "relative",
                  }}
                >
                  <div
                    style={{
                      width: `${targetW}px`,
                      height: `${totalH}px`,
                      transform: `scale(${scale})`,
                      transformOrigin: "top left",
                    }}
                    className={`absolute top-0 left-0 flex flex-col bg-zinc-900 border border-zinc-700/80 shadow-2xl overflow-hidden ${
                      fullscreenViewport === "desktop" ? "rounded-xl" : "rounded-2xl"
                    }`}
                  >
                    {/* Minimal Neutral Chrome Bar (NO Mac colored dots, NO fake hardware) */}
                    <div className="h-8 bg-zinc-900 border-b border-zinc-800 px-3 flex items-center justify-between gap-3 shrink-0 select-none">
                      <div className="flex items-center gap-1.5 text-xs text-zinc-400 font-medium">
                        {fullscreenViewport === "desktop" ? (
                          <Monitor className="h-3.5 w-3.5 opacity-70" />
                        ) : fullscreenViewport === "tablet" ? (
                          <Tablet className="h-3.5 w-3.5 opacity-70" />
                        ) : (
                          <Smartphone className="h-3.5 w-3.5 opacity-70" />
                        )}
                        <span className="capitalize">{fullscreenViewport}</span>
                      </div>

                      <div className="flex-1 max-w-sm mx-auto h-5 bg-zinc-950 border border-zinc-800 rounded px-2.5 text-[10px] font-mono text-zinc-400 flex items-center gap-1.5 overflow-hidden">
                        <span className="text-zinc-500">https://</span>
                        <span className="truncate">profolio.lk/@{profile?.subdomain || "preview"}</span>
                      </div>

                      <span className="font-mono text-[10px] text-zinc-400 shrink-0">
                        {targetW} × {viewportH}
                      </span>
                    </div>

                    {/* Clean Iframe Viewport */}
                    <div className="flex-1 overflow-hidden relative bg-white min-h-0">
                      <iframe
                        ref={fullscreenIframeRef}
                        srcDoc={getHtmlWithBridge(latestHtmlRef.current)}
                        onLoad={handleFullscreenIframeLoad}
                        className="w-full h-full border-none block"
                        title={`Fullscreen ${fullscreenViewport} Preview`}
                        sandbox="allow-scripts"
                      />
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Floating Exit Button */}
            <button
              type="button"
              onClick={() => setIsFullscreen(false)}
              className="fixed bottom-6 left-1/2 -translate-x-1/2 z-60 flex items-center gap-2 px-4 py-2 rounded-full bg-zinc-900/90 text-zinc-100 hover:bg-zinc-800 hover:text-white shadow-2xl border border-zinc-700/80 text-xs font-medium backdrop-blur transition-all duration-150 hover:scale-105 active:scale-95 cursor-pointer"
              title="Exit Fullscreen Preview (Esc)"
            >
              <Minimize2 className="h-3.5 w-3.5 text-primary" />
              <span>Exit Fullscreen (Esc)</span>
            </button>
          </div>
        </div>
      )}
    </>
  );
}

